import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Ban } from 'lucide-react'
import type { Point } from '../types'
import type { Client } from '@/modules/clients/types'
import type { User } from '@/modules/users/types'
import type { BranchOffice } from '@/modules/branches/types'
import { ConfirmDialog } from '@/shared/components/ui/molecules/ConfirmDialog'

interface PointDetailProps {
  point: Point
  client: Client | null
  user: User | null
  branch: BranchOffice | null
  onInvalidate: () => void
  isInvalidating: boolean
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('es-CL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

function formatDateTime(dateStr: string): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('es-CL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function PointDetail({ point, client, user, branch, onInvalidate, isInvalidating }: PointDetailProps) {
  const [showInvalidateDialog, setShowInvalidateDialog] = useState(false)

  const isAdd = point.transaction_type === 'add'
  const canInvalidate = point.is_valid && point.used_points === 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/points" className="text-gray-500 hover:text-gray-700">
            <ArrowLeft size={20} />
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">
              Transacción #{point.id}
            </h1>
            <span
              className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                isAdd
                  ? 'bg-sky-100 text-sky-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {isAdd ? 'Acumulación' : 'Canje'}
            </span>
            <span
              className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                point.is_valid
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {point.is_valid ? 'Válida' : 'Anulada'}
            </span>
          </div>
        </div>
        {canInvalidate && (
          <button
            onClick={() => setShowInvalidateDialog(true)}
            className="btn bg-red-600 text-white hover:bg-red-700 flex items-center gap-1.5"
          >
            <Ban size={16} />
            Anular
          </button>
        )}
      </div>

      {/* Summary card */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4">Resumen</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-500">Puntos</p>
            <p className="text-3xl font-bold text-primary-600">{point.points}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Monto</p>
            <p className="text-2xl font-semibold">${point.amount.toLocaleString('es-CL')}</p>
          </div>
          {isAdd && (
            <>
              <div>
                <p className="text-sm text-gray-500">Puntos usados</p>
                <p className="text-2xl font-semibold">{point.used_points}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Puntos restantes</p>
                <p className="text-2xl font-semibold">{point.points - point.used_points}</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Info card */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4">Información</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-500">Fecha de transacción</p>
            <p className="font-medium">{formatDateTime(point.datetime_transaction)}</p>
          </div>
          {isAdd && (
            <div>
              <p className="text-sm text-gray-500">Fecha de expiración</p>
              <p className="font-medium">{formatDate(point.datetime_expiration)}</p>
            </div>
          )}
          <div>
            <p className="text-sm text-gray-500">Comentario</p>
            <p className="font-medium">{point.comment || '-'}</p>
          </div>
        </div>
      </div>

      {/* References card */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4">Referencias</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-500">Cliente</p>
            <Link
              to={`/clients/${point.id_client}`}
              className="text-primary-600 hover:text-primary-700"
            >
              {client ? (
                <span className="font-medium">{client.name} {client.last_name}</span>
              ) : (
                <span className="font-medium">#{point.id_client}</span>
              )}
            </Link>
            {client && (
              <p className="text-xs text-gray-400 mt-0.5">{client.rut} · {client.email}</p>
            )}
          </div>
          <div>
            <p className="text-sm text-gray-500">Usuario</p>
            {user ? (
              <>
                <p className="font-medium">{user.name} {user.last_name}</p>
                <p className="text-xs text-gray-400 mt-0.5">{user.email} · {user.role}</p>
              </>
            ) : (
              <p className="font-medium">#{point.id_user}</p>
            )}
          </div>
          <div>
            <p className="text-sm text-gray-500">Sucursal</p>
            {branch ? (
              <>
                <p className="font-medium">{branch.commune}</p>
                <p className="text-xs text-gray-400 mt-0.5">{branch.address}</p>
              </>
            ) : (
              <p className="font-medium">#{point.id_branch_office}</p>
            )}
          </div>
          <div>
            <p className="text-sm text-gray-500">ID Transacción</p>
            <p className="font-medium">{point.id}</p>
          </div>
        </div>
      </div>

      {/* Invalidate confirmation dialog */}
      <ConfirmDialog
        isOpen={showInvalidateDialog}
        title="Anular transacción"
        message={`Se anulará la transacción #${point.id} de ${point.points} puntos. Esta acción no se puede deshacer.`}
        confirmLabel="Anular"
        onConfirm={() => {
          setShowInvalidateDialog(false)
          onInvalidate()
        }}
        onCancel={() => setShowInvalidateDialog(false)}
        isLoading={isInvalidating}
      />
    </div>
  )
}
